import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { Stage } from "views/settings-crm/types/stage";
import { LeadsFilters } from "shared/app/LeadsTableView/types";
import { useChangeLeadStage } from "shared/app/LeadsTableView/hooks/useChangeLeadStage";
import { PipelineColumn } from "./PipelineColumn";

interface LeadsPipelineViewProps {
  stagesData: Stage[];
  filters?: LeadsFilters;
  search?: string;
  assigneeIds: number[] | null;
  starred: boolean;
  enabled: boolean;
}

export const LeadsPipelineView = ({
  stagesData,
  filters,
  search,
  assigneeIds,
  starred,
  enabled
}: LeadsPipelineViewProps) => {
  const { mutate: changeStage } = useChangeLeadStage();

  const boardRef = useRef<HTMLDivElement>(null);
  const [boardTop, setBoardTop] = useState(0);

  // The FilterBadge row above the board mounts/unmounts with active filters,
  // which shifts the board's top — so re-measure when this toggles too.
  const hasActiveFilters = Boolean(
    filters?.stages?.length ||
    filters?.properties?.length ||
    filters?.leadSources?.length ||
    filters?.activityTypes?.length ||
    filters?.createdWithin ||
    filters?.createdAfter ||
    filters?.createdBefore
  );

  // Pin the board to the viewport so the board itself (not the page, and not
  // each column) is the vertical scroll container — that's what lets
  // @hello-pangea/dnd auto-scroll while dragging a card up/down, mirroring how
  // the board is the horizontal scroller below. Columns keep their natural
  // height; only the board scrolls. Re-measure on resize and when the filter
  // row toggles.
  useLayoutEffect(() => {
    const measure = () => {
      const board = boardRef.current;
      if (board) setBoardTop(board.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [hasActiveFilters]);

  // Make the board itself the horizontal scroll container. Its flex ancestors
  // default to min-width:auto, so the columns' width propagates up and a far
  // ancestor becomes the scroller instead of the board — which breaks
  // @hello-pangea/dnd's auto-scroll and drop-position tracking (it follows the
  // board, not the element that actually scrolls). Letting those ancestors
  // shrink makes the board clip+scroll, so the library scrolls the right element
  // and the placeholder lands in the hovered column. Scoped to this view and
  // reverted on unmount so other pages are untouched.
  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;

    const touched: HTMLElement[] = [];
    let el = board.parentElement;
    while (el && el !== document.body) {
      const { overflowX } = getComputedStyle(el);
      const isScroller =
        (overflowX === "auto" || overflowX === "scroll") &&
        el.scrollWidth > el.clientWidth;
      if (isScroller) break; // reached the outer scroll container — leave it
      el.style.minWidth = "0";
      touched.push(el);
      el = el.parentElement;
    }

    return () => {
      touched.forEach((node) => {
        node.style.minWidth = "";
      });
    };
  }, []);

  // Show every stage by default; when stage filters are applied, only those
  // stages become columns.
  const visibleStages = useMemo(() => {
    const selectedStages = filters?.stages;
    if (!selectedStages?.length) return stagesData;
    const selectedIds = new Set(selectedStages.map((selected) => selected.id));
    return stagesData.filter((stage) => selectedIds.has(String(stage.id)));
  }, [stagesData, filters?.stages]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result;
      if (!destination || source.droppableId === destination.droppableId)
        return;

      const destStage = stagesData.find(
        (stage) => String(stage.id) === destination.droppableId
      );
      if (!destStage) return;

      changeStage({ leadId: Number(draggableId), stage: destStage });
    },
    [stagesData, changeStage]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
        ref={boardRef}
        className="flex min-w-0 items-start gap-4 overflow-auto pb-4"
        style={
          boardTop
            ? { height: `calc(100dvh - ${boardTop}px - 16px)` }
            : undefined
        }
      >
        {visibleStages.map((stage) => (
          <PipelineColumn
            key={stage.id}
            stage={stage}
            filters={filters}
            search={search}
            assigneeIds={assigneeIds}
            starred={starred}
            enabled={enabled}
            scrollRootRef={boardRef}
          />
        ))}
      </div>
    </DragDropContext>
  );
};
